import { 
  Button, TextField, Alert, CircularProgress, 
  Box, Typography, IconButton, Tooltip, Paper, Divider,
  LinearProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import endpoints from "../../../environment/endpoints";
import FetchService from "../../../services/FetchService";
import ContenderFormControl, { Contender } from "./contenderFormControl/contenderFormControl";
import style from "./wizard.module.css";

export default function Wizard() {
  const fetchService = new FetchService();
  const [page, setPage] = useState(0);
  const [shortKeys, setShortKeys]: [any, React.Dispatch<any>] = useState();
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    contenders: [] as Contender[],
  });

  const completeWizard = React.useCallback(async () => {
    setIsSubmitting(true);
    const result = await fetchService.post(endpoints.createMashList, formData);
    if (result.success) {
      setShortKeys(result.data);

      // Upload images for contenders that have them
      const contendersWithImages = formData.contenders.filter(c => c.imageFile);
      if (contendersWithImages.length > 0) {
        const mashListRes = await fetchService.get(endpoints.getMashList, result.data.id);
        if (mashListRes.success && mashListRes.data.contenders) {
          const serverContenders = mashListRes.data.contenders;
          
          for (let i = 0; i < formData.contenders.length; i++) {
            const localContender = formData.contenders[i];
            if (localContender.imageFile) {
              const match = serverContenders.find(
                (sc: any) => sc.title === localContender.name
              );
              if (match) {
                setImageUploadProgress(`Uploading image for "${localContender.name}"...`);
                await fetchService.uploadFile(
                  endpoints.uploadContenderImage,
                  localContender.imageFile,
                  match.id
                );
              }
            }
          }
          setImageUploadProgress(null);
        }
      }

      setPage(2);
    } else {
      const errMessage = typeof result.errors === 'string' 
        ? result.errors 
        : (Array.isArray(result.errors) ? (result.errors[0] as any)?.message : null);
      setError(errMessage || "Failed to create mash list. Are you logged in?");
    }
    setIsSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const nextPage = React.useCallback(async () => {
    setError("");
    if (page === 0) {
      if (!formData.name.trim()) {
        setError("Please enter a name for your mash-list.");
        return;
      }
      setPage(1);
    } else if (page === 1) {
      if (!formData.contenders || formData.contenders.length < 2) {
        setError("Please add at least 2 contenders to your mash-list.");
        return;
      }
      await completeWizard();
    }
  }, [page, formData, completeWizard]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        const target = e.target as HTMLElement;
        const isInsideDialog = target && target.closest('.MuiDialog-root');
        if (!isInsideDialog && page < 2) {
          nextPage();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [nextPage, page]);

  const catchFormData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.name;
    const value = event.target.value;

    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleContendersChange = (contenders: Contender[]) => {
    setFormData({
      ...formData,
      contenders: contenders,
    });
  };

  const stepContent = () => {
    switch (page) {
      case 0:
        return (
          <>
            <h1 className={style.h1}>Give your mash-list a name...</h1>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <div className={style.textField}>
              <TextField
                sx={{ maxWidth: 600, width: '100%' }}
                id="name"
                name="name"
                label="Name"
                variant="outlined"
                onChange={catchFormData}
                value={formData.name}
              />
            </div>

          </>
        );
      case 1:
        return (
          <>
            <h1 className={style.h1}>Add some contenders...</h1>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <div className={style.contenderFormControl}>
              <ContenderFormControl
                contenders={formData.contenders}
                onChange={handleContendersChange}
              ></ContenderFormControl>
            </div>
          </>
        );
      case 2:
        const votingUrl = `${window.location.origin}/mashlist/${shortKeys!.viewShortKey}`;
        return (
          <Box className={style.successContainer}>
            <Box 
              sx={{ 
                maxWidth: 500, 
                width: '100%', 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3
              }}
            >
              <TaskAltIcon color="success" sx={{ fontSize: 100 }} />
              
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Mashlist Created!
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  "{formData.name}" is ready for mashing.
                </Typography>
              </Box>

              <Paper variant="outlined" sx={{ p: 3, width: '100%', bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" gutterBottom align="left">
                  Public Voting Link:
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      flexGrow: 1, 
                      fontFamily: 'monospace', 
                      bgcolor: 'background.paper', 
                      p: 1, 
                      borderRadius: 1,
                      textAlign: 'left',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {votingUrl}
                  </Typography>
                  <Tooltip title="Copy Link">
                    <IconButton onClick={() => navigator.clipboard.writeText(votingUrl)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              <Divider sx={{ width: '100%' }} />

              <Box display="flex" justifyContent="center" gap={2} width="100%">
                <Button 
                  variant="contained" 
                  color="secondary" 
                  component={Link} 
                  to={`/mashlist/${shortKeys!.id}/manage`}
                  sx={{ px: 4, whiteSpace: 'nowrap' }}
                >
                  Go to Management
                </Button>
                <Button 
                  variant="outlined" 
                  component={Link} 
                  to="/mashlist/list"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  View All Lists
                </Button>
              </Box>
            </Box>
          </Box>
        );



    }
  };



  const renderButtons = () => {
    if (page < 2) {
      let buttons = [];
      if (page > 0) {
        buttons.push(
          <Button variant="contained" className={style.left} onClick={() => setPage(page - 1)}>
            Prev
          </Button>
        );
      }

      buttons.push(
        <Button variant="contained" className={style.right} onClick={nextPage} disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (page === 1 ? "Finish" : "Next")}
        </Button>
      );
      return buttons;
    }
  };

  return (
    <div className={style.container}>
      {stepContent()}
      {imageUploadProgress && (
        <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
          <Typography variant="body2" color="textSecondary" textAlign="center" mb={1}>
            {imageUploadProgress}
          </Typography>
          <LinearProgress color="secondary" />
        </Box>
      )}
      {renderButtons()}
    </div>
  );
}
